import { CaseContextType } from "../types";
import devLogger from "./devLogger";

/**
 * Extracts a readable string value from different field types
 */
export function getValue(
  field:
    | string
    | { custom: boolean; customValue?: string | undefined }
    | undefined
    | null
): string {
  if (typeof field === "string") {
    return field;
  }

  if (field?.custom) {
    return field.customValue || "Not Available";
  }

  return "Not Available";
}

/**
 * Prepares the case payload object from context data
 */
export function prepareCasePayload(
  contextData: Omit<CaseContextType, "updateCaseData">
) {
  const {
    abuserDetails,
    caseType,
    subCaseType,
    incidentLocation,
    incidentDescription,
    detailedIncidentLocation,
    hasAbuserInfo,
    reporterDetails,
    incidentDate,
    supportServices,
  } = contextData;

  return {
    caseName: getValue(caseType) || "Unknown Case",
    caseType: getValue(subCaseType) || "Unknown Type",
    incidentDescription: {
      date: incidentDate || "Unknown Date",
      location: getValue(incidentLocation),
      digitalcaseLinks: incidentDescription.digitalcaseLinks,
      detailedLocation: {
        state: detailedIncidentLocation.state,
        county: detailedIncidentLocation.county,
        city: detailedIncidentLocation.city,
      },
      description:
        incidentDescription.incidentNarration || "No description provided",
    },
    reporterDetails: {
      role: reporterDetails.role,
      gender: reporterDetails.gender,
      age: reporterDetails.age,
      phone:
        reporterDetails?.phone?.length > 0 ? reporterDetails.phone : undefined,
      email: reporterDetails.email,
      socials: reporterDetails.socials,
      ethnicity: getValue(reporterDetails.ethnicity),
    },
    hasAbuserInfo,
    abuserDetails: hasAbuserInfo
      ? {
          name: abuserDetails.name,
          nickname: abuserDetails.alias,
          gender: abuserDetails.gender,
          refAge: abuserDetails.refAge,
          vehicleInfo: abuserDetails.hasAbuserVehicleInfo
            ? {
                brand: getValue(abuserDetails.vehicleInfo?.brand),
                model: abuserDetails.vehicleInfo?.model || "Unknown Model",
                carColor: getValue(abuserDetails.vehicleInfo?.carColor),
                licensePlate:
                  abuserDetails.vehicleInfo?.licensePlate || "Unknown Plate",
              }
            : null,
          relationToVictim: getValue(abuserDetails.relation),
          hasSpecialAuthority: abuserDetails.hasSpecialAuthority,
          specialAuthorityPosition: getValue(abuserDetails.authorityPosition),
          phyDescription: abuserDetails.phyDescription,
          hasAbuserVehicleInfo: abuserDetails.hasAbuserVehicleInfo,
          occupation: getValue(abuserDetails.occupation),
          socials: abuserDetails.socials,
          ethnicity: getValue(abuserDetails.ethnicity),
        }
      : null,
    NeedAnAttorney: supportServices?.NeedAnAttorney ?? null,
    NeedCounseling: supportServices?.NeedCounseling ?? null,
    NeedPrivateInvestigator: supportServices?.NeedPrivateInvestigator ?? null,
  };
}

/**
 * Creates and populates a FormData object with case data and files
 */
export async function prepareReportFormData(
  contextData: Omit<CaseContextType, "updateCaseData">
) {
  devLogger.log("🚀 ~ contextData:", contextData);
  const { incidentDescription, hasAbuserInfo, abuserDetails } = contextData;

  // Create the case payload object
  const casePayload = prepareCasePayload(contextData);

  // Create a FormData object to send both the JSON payload and files
  const formData = new FormData();

  // Add the JSON payload as a string with the name 'createReportDto'
  formData.append("createReportDto", JSON.stringify(casePayload));

  // Handle incident voice narration (audio) files
  if (incidentDescription.incidentVoiceNarration) {
    // Case 1: Handle Blob (convert to File)
    if (Array.isArray(incidentDescription.incidentVoiceNarration)) {
      incidentDescription.incidentVoiceNarration.forEach((file) => {
        if (file instanceof File) {
          formData.append("incidentVoiceNarration", file);
        }
      });
    }
  }

  // Handle incident photo evidence
  if (incidentDescription.incidentPhotoEvidence) {
    if (Array.isArray(incidentDescription.incidentPhotoEvidence)) {
      incidentDescription.incidentPhotoEvidence.forEach((file) => {
        if (file instanceof File) {
          formData.append("incidentPhotoEvidence", file);
        }
      });
    }
  }

  // Handle abuser photo description
  if (hasAbuserInfo && abuserDetails.photoDescription) {
    if (Array.isArray(abuserDetails.photoDescription)) {
      abuserDetails.photoDescription.forEach((file) => {
        if (file instanceof File) {
          formData.append("abuserPhotoDescription", file);
        }
      });
    }
  }

  return formData;
}
